import BasicRecord from "./BasicRecord";

function union<T>(array1: T[], array2: T[]): T[] {
    return [...new Set([...array1, ...array2])];
}

function intersection<T>(array1: T[], array2: T[]): T[] {
    return array1.filter(item => array2.includes(item));
}

function difference<T>(array1: T[], array2: T[]): T[] {
    return array1.filter(item => !array2.includes(item));
}

/**
 * Sorts an array of objects by a specific property name
 * @param array array to be sorted
 * @param propertyName property name to be sorted by
 */
function sortArrayOfObjectsByPropertyName<T extends { [k in U]: number; }, U extends keyof T>(
    array: T[], propertyName: U
): T[] {
    return array.sort((first, second) => first[propertyName] - second[propertyName]);
}

/**
 * Removes duplicates from given array based on the property name
 * @param array array to be removed from duplicates
 * @param propertyName property name to be compared
 */
function removeDuplicatesFromArrayOfObjects<
    T extends({ [k in U]: number; } | { [k in U]: string; }),
    U extends keyof T
>(array: T[], propertyName: U): T[] {
    return array.reduce(({ memory, result }, item) => {
        if (memory[(item[propertyName]) as string]) {
            return { memory, result };
        }

        result.push(item);
        memory[(item[propertyName]) as string] = true;

        return { memory, result };
    }, {
        memory: {} as BasicRecord<string | boolean>,
        result: [] as T[]
    }).result;
}

type ElementLike = JSX.Element | string;

/**
 * Applies join operation to DOM elements
 * @param array array to apply join
 * @param separator separator to separate items
 */
function joinDOMElements(array: ElementLike[], separator: ElementLike): ElementLike[] {
    return array.reduce<ElementLike[]>((prev, curr, index) => {
        if (index > 0) {
            prev.push(separator);
        }
        prev.push(curr);
        return prev;
    }, []);
}

/**
 * Creates an array of sorted numbers following the same increment value
 *
 * Example:
 * const arr = populateArrayOfConstantIncrements(0, 1.0, 5)
 * console.log(arr) // [0, 0.2, 0.4, 0.6, 0.8, 1.0]
 *
 * @param start starting number
 * @param end last number of the series
 * @param steps number of steps
 * @param {Options} options
 *
 * @typedef {Object} Options
 * @property excludeStart Whether to exclude the starting number in the result array
 * @property excludeEnd Whether to exclude the ending number in the result array
 */
function populateArrayOfConstantIncrements(
    start: number,
    end: number,
    steps: number,
    { excludeStart = false, excludeEnd = false }: {
        excludeStart?: boolean;
        excludeEnd?: boolean;
    } = {}
) {
    if (steps <= 0) {
        throw new RangeError(`Invalid number of steps: ${steps}. Steps must be a positive number`);
    }
    if (start >= end) {
        return [];
    }

    const arr = excludeStart ? [] : [start];
    const incrementValue = (end - start) / steps;

    for (let i = 1; i < steps; i++) {
        arr.push(start + i * incrementValue);
    }

    return excludeEnd ? arr : arr.concat(end);
}

function arrayRemove<T>(array: T[], callback: (item: T) => boolean): T | null;
function arrayRemove<T extends { _id: string; }>(array: T[], id: string): T | null;
function arrayRemove<T>(array: T[], cbOrId: ((item: T) => boolean) | string): T | null {
    const callback = typeof cbOrId === "function"
        ? cbOrId
        : (item: T) => (item as unknown as { _id: string; })._id === cbOrId;

    const idx = array.findIndex(callback);

    if (idx === -1) {
        return null;
    }

    return array.splice(idx, 1)[0];
}

/**
 * Compare two list according to cb
 */
function arrayEqual<T>(
    arr1: T[],
    arr2: T[],
    cb: (item1: T, item2: T) => boolean = (item1, item2) => item1 === item2
): boolean {
    if (arr1.length !== arr2.length) {
        return false;
    }

    for (let i = 0; i < arr1.length; i++) {
        if (!cb(arr1[i], arr2[i])) {
            return false;
        }
    }

    return true;
}

/*
 * Normalizes an array by given key
 */
function normalizeArray<T extends { [k in K]: string }, K extends keyof T>(arr: T[], key: K): BasicRecord<T> {
    return arr.reduce<BasicRecord<T>>((acc, cur) => {
        acc[cur[key]] = cur;
        return acc;
    }, {});
}

/*
 * Creates an object composed of keys generated from given array of object's key
 */
function groupBy<T extends { [k in K]: string }, K extends keyof T>(arr: T[], key: K): BasicRecord<T[]> {
    return arr.reduce<BasicRecord<T[]>>((acc, cur) => {
        (acc[cur[key]] ??= []).push(cur);
        return acc;
    }, {});
}

function containsAny<T>(array1: T[], array2: T[]) {
    return array1.some(item => array2.includes(item));
}

function containsAll<T>(array1: T[], array2: T[]) {
    return array2.every(item => array1.includes(item));
}

function addIfNotExistRemoveIfExist<T>(array: T[] | undefined | null, item: T) {
    if (!array) {
        return array;
    }

    if (array.includes(item)) {
        const newArray = [...array];
        newArray.splice(newArray.indexOf(item), 1);
        return newArray;
    }

    return [...array, item];
}

function shuffleArray<T>(array: T[]) {
    const shuffledArray = [...array];
    let currentIndex = shuffledArray.length;
    let randomIndex;

    while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;

        [shuffledArray[currentIndex], shuffledArray[randomIndex]] = [
            shuffledArray[randomIndex], shuffledArray[currentIndex]
        ];
    }

    return shuffledArray;
}

export {
    ElementLike,
    union,
    intersection,
    difference,
    sortArrayOfObjectsByPropertyName,
    removeDuplicatesFromArrayOfObjects,
    joinDOMElements,
    populateArrayOfConstantIncrements,
    arrayRemove,
    arrayEqual,
    normalizeArray,
    groupBy,
    containsAny,
    containsAll,
    addIfNotExistRemoveIfExist,
    shuffleArray
};
